import React, { useCallback, useEffect, useState } from "react";
import { Bookmark, FilePlus2, Trash2, Upload } from "lucide-react";
import {
  DRAFT_LIMITS,
  type Draft,
  deleteDraft,
  listDrafts,
  renameDraft,
  saveDraft,
  subscribeToDraftsChange,
} from "src/lib/draftsStore";

export interface DraftsSectionProps<T> {
  /** Stable per-form key. Two forms must NOT share a key. */
  storageKey: string;
  /** Snapshot taken when the user clicks "Save". */
  currentValues: T;
  /** Restore handler. Apply each field back into the form's local state. */
  onLoad: (values: T) => void;
  /** Suggest a default name in the rename / save prompt. */
  suggestName?: (values: T) => string;
  /** Disable save when the form is empty. */
  isEmpty?: (values: T) => boolean;
  /** Heading. Default: "Drafts". */
  title?: string;
  /** Sub-heading. Default explains the on-device storage. */
  subtitle?: string;
  /** Optional accent class for the icon badge — default amber. */
  accentClassName?: string;
  /**
   * Hide the inline "Save current as draft" button — useful when the parent
   * form already provides a save action (e.g. next to its submit button).
   */
  showSaveButton?: boolean;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function DraftsSection<T>({
  storageKey,
  currentValues,
  onLoad,
  suggestName,
  isEmpty,
  title = "Drafts",
  subtitle,
  accentClassName,
  showSaveButton = true,
}: DraftsSectionProps<T>) {
  const [drafts, setDrafts] = useState<Draft<T>[]>(() =>
    listDrafts<T>(storageKey),
  );
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setDrafts(listDrafts<T>(storageKey));
  }, [storageKey]);

  // Same-tab + cross-tab sync. Same-tab fires when the parent saves via
  // `promptAndSaveDraft`; cross-tab fires when another tab edits.
  useEffect(() => {
    return subscribeToDraftsChange(storageKey, refresh);
  }, [storageKey, refresh]);

  // Auto-clear flash after a few seconds.
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(null), 3500);
    return () => window.clearTimeout(id);
  }, [flash]);

  const empty = isEmpty?.(currentValues) ?? false;

  const handleSave = () => {
    setError(null);
    const suggested =
      (suggestName?.(currentValues) ?? "").trim() || "Untitled draft";
    const name = window.prompt("Name this draft:", suggested);
    if (name == null) return;
    try {
      const d = saveDraft(storageKey, name, currentValues);
      refresh();
      setFlash(`Saved "${d.name}".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save draft");
    }
  };

  const handleRename = (d: Draft<T>) => {
    setError(null);
    const name = window.prompt("Rename draft:", d.name);
    if (name == null) return;
    if (name.trim() === d.name) return;
    try {
      renameDraft<T>(storageKey, d.id, name);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not rename draft");
    }
  };

  const handleDelete = (d: Draft<T>) => {
    if (!window.confirm(`Delete draft "${d.name}"?`)) return;
    try {
      deleteDraft<T>(storageKey, d.id);
      refresh();
      setFlash(`Deleted "${d.name}".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete draft");
    }
  };

  const handleLoad = (d: Draft<T>) => {
    onLoad(d.values);
    setFlash(`Loaded "${d.name}" into the form.`);
  };

  return (
    <section
      className="rounded-[1.75rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-7"
      aria-labelledby="drafts-heading"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-[0_3px_0_#B45309] ${
              accentClassName ?? "bg-[#F59E0B]"
            }`}
            aria-hidden
          >
            <Bookmark className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2
              id="drafts-heading"
              className="text-lg font-extrabold text-[#3C3C3C] sm:text-xl"
            >
              {title}
            </h2>
            <p className="text-xs font-bold text-[#777777]">
              {subtitle ??
                `Saved on this device only · ${drafts.length}/${DRAFT_LIMITS.perKey}`}
            </p>
          </div>
        </div>

        {showSaveButton && (
          <button
            type="button"
            onClick={handleSave}
            disabled={empty}
            title={
              empty
                ? "Fill in the form first"
                : "Save the form's current values as a draft"
            }
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border-2 border-[#1CB0F6] bg-[#E8F6FE] px-4 text-sm font-extrabold uppercase tracking-wide text-[#1899D6] shadow-[0_3px_0_#7CC4F0] active:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0"
          >
            <FilePlus2 className="h-4 w-4" aria-hidden />
            Save current as draft
          </button>
        )}
      </div>

      {error && (
        <p className="mb-3 rounded-xl bg-[#FFEDED] px-3 py-2 text-sm font-extrabold text-[#FF4B4B]">
          {error}
        </p>
      )}
      {flash && !error && (
        <p className="mb-3 rounded-xl bg-[#E8F8E0] px-3 py-2 text-sm font-extrabold text-[#3F9600]">
          {flash}
        </p>
      )}

      {drafts.length === 0 ? (
        <p className="rounded-2xl bg-[#FAFAFA] px-4 py-6 text-center text-sm font-bold text-[#777777]">
          No drafts yet. Fill in the form and tap{" "}
          <strong className="font-extrabold text-[#1899D6]">
            Save current as draft
          </strong>
          .
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {drafts.map((d) => (
            <li
              key={d.id}
              className="flex flex-col gap-2 rounded-2xl border-2 border-[#E5E5E5] bg-white p-3 sm:flex-row sm:items-center sm:gap-3 sm:p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-extrabold text-[#3C3C3C]">
                  {d.name}
                </p>
                <p className="text-xs font-bold text-[#777777]">
                  Saved {relativeTime(d.updatedAt)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                <button
                  type="button"
                  onClick={() => handleLoad(d)}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl border-2 border-[#58CC02] bg-[#E8F8E0] px-3 py-2 text-sm font-extrabold text-[#3F9600] shadow-[0_3px_0_#A6E07C] active:translate-y-0.5"
                  title="Load into form"
                >
                  <Upload className="h-4 w-4" aria-hidden />
                  Load
                </button>
                <button
                  type="button"
                  onClick={() => handleRename(d)}
                  className="inline-flex min-h-[40px] items-center justify-center rounded-xl border-2 border-[#E5E5E5] bg-white px-3 py-2 text-sm font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] active:translate-y-0.5"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(d)}
                  className="inline-flex min-h-[40px] items-center justify-center rounded-xl border-2 border-[#FCA5A5] bg-[#FFEDED] px-3 py-2 text-sm font-extrabold text-[#B91C1C] shadow-[0_3px_0_#FCA5A5] active:translate-y-0.5"
                  title="Delete draft"
                  aria-label={`Delete draft ${d.name}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default DraftsSection;
